import {InjectionToken} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map, shareReplay} from 'rxjs/operators';
import {ValidationRule} from '../rules/rules';

export const VALIDATION_CONTEXTS = new InjectionToken<Array<ValidationContext>>('VALIDATION_CONTEXTS');

export class ValidationContextField {
    protected readonly rules$ = new BehaviorSubject<ReadonlyArray<ValidationRule>>([]);

    protected readonly rulesObservable$: Observable<ReadonlyArray<ValidationRule>>;

    public constructor(
        public readonly name?: string
    ) {
        this.rulesObservable$ = this.rules$
            .pipe(
                shareReplay(1),
            );
    }

    public swapRules($value: ReadonlyArray<ValidationRule>): void {
        this.rules$.next($value);
    }

    public addRule($rule: ValidationRule): void {
        this.rules$.next(
            this.rules$.value.concat($rule)
        );
    }

    public removeRule($rule: ValidationRule): void {
        const index = this.rules$.value.indexOf($rule);
        if (index > -1) {
            this.rules$.next(this.rules$.value.slice(0, index).concat(this.rules$.value.slice(index + 1)));
        }
    }

    public clean(): void {
        this.rules$.next([]);
    }

    public doDestroy(): void {
        this.rules$.complete();
    }

    public getRulesSnapshot(): ReadonlyArray<ValidationRule> {
        return this.rules$.value;
    }

    public getRules$(): Observable<ReadonlyArray<ValidationRule>> {
        return this.rulesObservable$;
    }
}

export class ValidationContext {
    protected readonly fields$ = new BehaviorSubject<Record<string, ValidationContextField>>({});
    protected readonly fieldsObservable$: Observable<Record<string, ValidationContextField>>;

    constructor(
        public readonly name?: string
    ) {
        this.fieldsObservable$ = this.fields$.pipe(shareReplay(1));
    }

    // Not sure if this is OK!
    public swapFields($value: Record<string, ValidationContextField>): void {
        this.fields$.next($value);
    }

    public changeField($name: string, $value: ValidationContextField): void {
        this.fields$.next({
            ...this.fields$.value,
            [$name]: $value,
        });
    }

    public removeField($name: string): void {
        if (this.fields$.value.hasOwnProperty($name)) {
            const newFields = {...this.fields$.value};
            delete newFields[$name];
            this.fields$.next(newFields);
        }
    }

    public clean(): void {
        this.fields$.next({});
    }

    public getFieldSnapshot($name: string): ValidationContextField {
        return this.fields$.value[$name];
    }

    public getField$($name: string): Observable<ValidationContextField> {
        return this.fields$
            .pipe(
                map($ => $[$name]),
                distinctUntilChanged(),
                shareReplay(1),
            );
    }

    public getOrCreateField$($name: string): Observable<ValidationContextField> {
        if (!this.fields$.value.hasOwnProperty($name)) {
            this.fields$.next({
                ...this.fields$.value,
                [$name]: new ValidationContextField($name)
            });
        }
        return this.getField$($name);
    }

    public doDestroy($recursive = true): void {
        if ($recursive) {
            Object
                .keys(this.fields$.value)
                .forEach($ => {
                    this.fields$.value[$].doDestroy();
                });
        }
        this.fields$.complete();
    }

    public getFields$(): Observable<Record<string, ValidationContextField>> {
        return this.fieldsObservable$;
    }
}
